import useEvent from "react-use-event-hook";

import {
  FutureBookingsConfirmOutcome,
  FutureBookingsImpactApi,
  IExtendableDto,
  IShiftStationOptionsChange,
  PASSENGER_STATION_FIELDS,
  SHIFT_STATION_OPTION_FIELDS,
} from "@ryde/shared";

import { useFutureBookingsConfirm } from "./use-future-bookings-confirm";
import { buildShiftStationOptionsChange } from "./use-future-bookings-confirm.utils";

/**
 * Bulk-edit counterpart of useFutureBookingsConfirm (RYDE-7929).
 *
 * In a bulk edit there are no react-hook-form dirty fields — the "trigger" is simply whether the
 * bulk payload carries a station field at all, so the checked form data doubles as the dirty map.
 *
 * Returns the same outcome enum as the single-edit hook. Bulk matters more here, not less: the update
 * is processed asynchronously in chunks, so a stale or failed preview has a wider blast radius.
 */
export const useBulkFutureBookingsConfirm = () => {
  const { confirmUpdateFutureBookingsStations } = useFutureBookingsConfirm();

  const confirmBulkFutureBookingsStations = useEvent(
    async ({
      entity,
      ids,
      formData,
    }: {
      entity: string;
      ids: number[];
      formData: Partial<IExtendableDto>;
    }): Promise<FutureBookingsConfirmOutcome> => {
      if (!ids?.length) {
        return FutureBookingsConfirmOutcome.NotApplicable;
      }

      if (entity === "shift") {
        const shiftStationOptionsChange = buildShiftStationOptionsChange(
          formData as IShiftStationOptionsChange,
        );

        return confirmUpdateFutureBookingsStations({
          isEdit: true,
          dirtyFields: formData as Record<string, unknown>,
          triggerFields: SHIFT_STATION_OPTION_FIELDS,
          getImpactCount: () =>
            FutureBookingsImpactApi.forShiftsBulk(
              ids,
              shiftStationOptionsChange,
            ),
        });
      }

      if (entity === "passenger") {
        return confirmUpdateFutureBookingsStations({
          isEdit: true,
          dirtyFields: formData as Record<string, unknown>,
          triggerFields: PASSENGER_STATION_FIELDS,
          getImpactCount: () =>
            FutureBookingsImpactApi.forPassengersBulk(ids, formData),
        });
      }

      return FutureBookingsConfirmOutcome.NotApplicable;
    },
  );

  return { confirmBulkFutureBookingsStations };
};
